import React, { lazy, Suspense } from 'react';

/** Fallback shown while a lazy route is loading */
export const RouteLoadFallback: React.FC = () => (
  <div className="flex items-center justify-center min-h-[280px]">
    <div className="text-gray-500 dark:text-gray-400">Loading...</div>
  </div>
);

/** Wraps children in Suspense with route fallback (for use around Routes that may render lazy components) */
export const LazySuspense: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Suspense fallback={<RouteLoadFallback />}>{children}</Suspense>
);

// ----- Reports -----
export const ReportGeneratorLazy = lazy(() =>
  import('./modules/report-generator').then((m) => ({ default: m.List }))
);
export const InvoicesByYearReportLazy = lazy(() =>
  import('./modules/reports/invoices-by-year').then((m) => ({ default: m.List }))
);
export const InvoicesPaymentAnalysisReportLazy = lazy(() =>
  import('./modules/reports/invoices-payment-analysis').then((m) => ({ default: m.List }))
);
export const ExpensesByYearReportLazy = lazy(() =>
  import('./modules/reports/expenses-by-year').then((m) => ({ default: m.List }))
);
export const UnpaidClientsReportLazy = lazy(() =>
  import('./modules/reports/unpaid').then((m) => ({ default: m.List }))
);
export const AppointmentsReportLazy = lazy(() =>
  import('./modules/reports/appointments-report').then((m) => ({ default: m.List }))
);
export const CallsReportLazy = lazy(() =>
  import('./modules/reports/calls-report').then((m) => ({ default: m.List }))
);
export const MissedCallsReportLazy = lazy(() =>
  import('./modules/reports/missed-calls').then((m) => ({ default: m.List }))
);
export const MonthlyServicesReportLazy = lazy(() =>
  import('./modules/reports/monthly-services').then((m) => ({ default: m.List }))
);
export const ClientTasksMonthlyReportLazy = lazy(() =>
  import('./modules/reports/client-tasks-monthly').then((m) => ({ default: m.List }))
);
export const TodosMonthlyReportPageLazy = lazy(() =>
  import('./modules/reports/todos-monthly').then((m) => ({ default: m.List }))
);

// ----- Settings & Admin -----
export const SettingsPageLazy = lazy(() => import('./modules/settings').then((m) => ({ default: m.default })));
export const FilesStoragePageLazy = lazy(() =>
  import('./modules/settings').then((m) => ({ default: m.FilesStoragePage }))
);
export const SystemEmailTemplatesPageLazy = lazy(() =>
  import('./modules/settings').then((m) => ({ default: m.SystemEmailTemplatesPage }))
);
export const EmailNotificationSettingsPageLazy = lazy(() =>
  import('./modules/settings').then((m) => ({ default: m.EmailNotificationSettingsPage }))
);
export const DatabaseBrowserPageLazy = lazy(() =>
  import('./modules/database-browser').then((m) => ({ default: m.DatabaseBrowserPage }))
);
export const GemiPreviewPageLazy = lazy(() =>
  import('./modules/gemi-preview').then((m) => ({ default: m.GemiPreviewPage }))
);
export const Directory11888PageLazy = lazy(() =>
  import('./modules/11888-preview').then((m) => ({
    default: m.Directory11888Page,
  }))
);
export const PresetsManagementListLazy = lazy(() =>
  import('./modules/presets-management').then((m) => ({ default: m.List }))
);

// ----- Imports -----
export const InvoiceXlsxLazy = lazy(() => import('./components/imports').then((m) => ({ default: m.InvoiceXlsx })));

// ----- Knowledge base wiki -----
export const KnowledgeBaseWikiLayoutLazy = lazy(() =>
  import('./modules/knowledge-base/components/KnowledgeBaseWikiLayout').then((m) => ({ default: m.default }))
);
export const KnowledgeBaseWikiHomeLazy = lazy(() =>
  import('./modules/knowledge-base/components/KnowledgeBaseWikiHome').then((m) => ({ default: m.default }))
);
export const KnowledgeBaseArticleViewLazy = lazy(() =>
  import('./modules/knowledge-base/components/KnowledgeBaseArticleView').then((m) => ({ default: m.default }))
);
export const KnowledgeBaseCategoriesFormLazy = lazy(() =>
  import('./modules/knowledge-base/categories').then((m) => ({ default: m.Form }))
);
export const KnowledgeBaseCategoriesDetailLazy = lazy(() =>
  import('./modules/knowledge-base/categories').then((m) => ({ default: m.Detail }))
);

// ----- Client portal -----
export const PortalDashboardLazy = lazy(() =>
  import('./client-portal/Dashboard').then((m) => ({ default: m.default }))
);
export const PortalInvoicesListLazy = lazy(() =>
  import('./client-portal/invoices/InvoicesList').then((m) => ({ default: m.default }))
);
export const PortalInvoiceDetailLazy = lazy(() =>
  import('./client-portal/invoices/InvoiceDetail').then((m) => ({ default: m.default }))
);
export const PortalSupportTicketsListLazy = lazy(() =>
  import('./client-portal/support/SupportTicketsList').then((m) => ({ default: m.default }))
);
export const PortalSupportTicketNewLazy = lazy(() =>
  import('./client-portal/support/SupportTicketNew').then((m) => ({ default: m.default }))
);
export const PortalSupportTicketDetailLazy = lazy(() =>
  import('./client-portal/support/SupportTicketDetail').then((m) => ({ default: m.default }))
);
export const PortalServicesListLazy = lazy(() =>
  import('./client-portal/PortalServicesList').then((m) => ({ default: m.default }))
);
export const PortalTasksListLazy = lazy(() =>
  import('./client-portal/tasks/PortalTasksList').then((m) => ({ default: m.default }))
);
export const PortalPackagesListLazy = lazy(() =>
  import('./client-portal/packages/PortalPackagesList').then((m) => ({ default: m.default }))
);
export const PortalProfileLazy = lazy(() =>
  import('./client-portal/profile/Profile').then((m) => ({ default: m.default }))
);
